"use client";

import type { ReactNode } from "react";

// Editing in two languages. Every editor has an English | العربية switch: English holds the item's own text and the
// settings both languages share (web address, images, dates, status); Arabic holds the Arabic text. Arabic fields are
// typed right to left with the Arabic font, so what you type looks the way it will on the site.

export type EditLang = "en" | "ar";

/** lang and dir for an input that holds text in a language. */
export const langProps = (lang: EditLang = "en") => ({ lang, dir: lang === "ar" ? ("rtl" as const) : ("ltr" as const) });

const NAMES: Record<EditLang, string> = { en: "English", ar: "العربية" };

/** The English | العربية switch. `missing` marks a language that still needs its text. */
export function LanguageTabs({ value, onChange, missing = {} }: { value: EditLang; onChange: (lang: EditLang) => void; missing?: Partial<Record<EditLang, boolean>> }) {
  return (
    <div role="radiogroup" aria-label="Language you are editing" className="inline-flex rounded-lg border border-midnight/20 bg-offwhite p-1">
      {(["en", "ar"] as const).map((lang) => (
        <button
          key={lang}
          type="button"
          role="radio"
          aria-checked={value === lang}
          onClick={() => onChange(lang)}
          className={`relative flex min-h-10 items-center gap-2 rounded-md px-4 text-sm font-bold transition-colors ${value === lang ? "bg-midnight text-offwhite" : "text-midnight/70 hover:text-midnight"}`}
        >
          <span lang={lang}>{NAMES[lang]}</span>
          {missing[lang] && (
            <>
              <span aria-hidden className="size-2 rounded-full bg-amber" />
              <span className="sr-only">(needs text)</span>
            </>
          )}
        </button>
      ))}
    </div>
  );
}

/** The bar under an editor's title: the switch and a line saying what this language's tab holds. */
export function LanguageBar({ lang, onChange, missing, arabicNote, children }: {
  lang: EditLang;
  onChange: (lang: EditLang) => void;
  missing?: Partial<Record<EditLang, boolean>>;
  /** Shown on the Arabic tab (default: where the shared settings are). */
  arabicNote?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-midnight/10 bg-offwhite p-3 sm:flex-row sm:items-center sm:p-4">
      <LanguageTabs value={lang} onChange={onChange} missing={missing} />
      <p className="text-sm text-midnight/70">
        {lang === "en" ? "English text, plus the settings both languages share." : (arabicNote ?? "Arabic text for /ar. The web address, images, dates and status are shared: change them on the English tab.")}
      </p>
      {children}
    </div>
  );
}
